import { connectWebPush, disconnectWebPush } from './utils/mobileWebPush';
import { captureCampaign, mobileApi, setMobileAccount, mobileOwner, sharedCartEnabled, reloadSharedCart, mutateSharedCart, reportMobileError, MobileCart } from './utils/mobileCommerce';
import { wishlistStore } from './utils/wishlistStore';
import React, { useState, useEffect, useCallback, useRef, lazy, Suspense } from 'react';
import { LanguageProvider } from './contexts/LanguageContext';
import MaintenanceMode from './components/MaintenanceMode';
import VacationBanner from './components/VacationBanner';
import CookieConsent from './components/CookieConsent';
import EasterPopup from './components/EasterPopup';
import PushConsent from './components/PushConsent';
import PageLoader from './components/PageLoader';
import Header from './components/Header';
import Footer from './components/Footer';
import type { StaggeredSetupData } from './components/StaggeredSetupAlert';
import type { MixedSet } from './components/MixedSetsSection';
const HomePage = lazy(() => import('./pages/HomePage'));

import { navigateTo } from './utils/navigation';
const PaymentSuccessPage = lazy(() => import('./pages/PaymentSuccessPage'));
const PaymentErrorPage = lazy(() => import('./pages/PaymentErrorPage'));
const PaymentTransferPage = lazy(() => import('./pages/PaymentTransferPage'));
const ConfirmOrderPage = lazy(() => import('./pages/ConfirmOrderPage'));

// Lazy load page components for better performance
const LoginPage = lazy(() => import('./pages/LoginPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const ProductDetailPage = lazy(() => import('./pages/ProductDetailPage'));
const CartPage = lazy(() => import('./pages/CartPage'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const ProductsPage = lazy(() => import('./pages/ProductsPage'));
const CustomerLoginPage = lazy(() => import('./pages/CustomerLoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const MyAccountPage = lazy(() => import('./pages/MyAccountPage'));
const WyszukiwaczPage = lazy(() => import('./pages/WyszukiwaczPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const AccountDeletionPage = lazy(() => import('./pages/AccountDeletionPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const ComplaintPage = lazy(() => import('./pages/ComplaintPage'));
const ReturnsPage = lazy(() => import('./pages/ReturnsPage'));
const FAQPage = lazy(() => import('./pages/FAQPage'));
const WheelParameterSeoPage = lazy(() => import('./pages/WheelParameterSeoPage'));
const Przelewy24SettingsPage = lazy(() => import('./pages/Przelewy24SettingsPage'));
const AllegroSettingsPage = lazy(() => import('./pages/AllegroSettingsPage'));
const DpdServicesPage = lazy(() => import('./pages/DpdServicesPage'));
const OLXSettingsPage = lazy(() => import('./pages/OLXSettingsPage'));
const FacebookSettingsPage = lazy(() => import('./pages/FacebookSettingsPage'));
const TikTokSettingsPage = lazy(() => import('./pages/TikTokSettingsPage'));
const InvoicingPage = lazy(() => import('./pages/InvoicingPage'));
const ShippingPage = lazy(() => import('./pages/ShippingPage'));
const DeliveryInfoPage = lazy(() => import('./pages/DeliveryInfoPage'));
const DpdShippingPage = lazy(() => import('./pages/DpdShippingPage'));
const ProductsAdminPageNew = lazy(() => import('./pages/ProductsAdminPageNew'));
const OrdersPage = lazy(() => import('./pages/OrdersPage'));
const IntegrationsPage = lazy(() => import('./pages/IntegrationsPage'));
const AllegroOffersPage = lazy(() => import('./pages/AllegroOffersPage'));
const OlxOrdersPage = lazy(() => import('./pages/OlxOrdersPage'));
const OlxAdvertsPage = lazy(() => import('./pages/OlxAdvertsPage'));
const WheelSizeApiPage = lazy(() => import('./pages/WheelSizeApiPage'));
const FitmentAdminPage = lazy(() => import('./pages/FitmentAdminPage'));
const BlogSection = lazy(() => import('./components/BlogSection'));
const BlogAdminPage = lazy(() => import('./pages/BlogAdminPage'));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));
const PricingAdminPage = lazy(() => import('./pages/PricingAdminPage'));
const SettingsAdminPage = lazy(() => import('./pages/SettingsAdminPage'));
const AiBackgroundLabPage = lazy(() => import('./pages/AiBackgroundLabPage'));
const BulkBgRemovalPage = lazy(() => import('./pages/BulkBgRemovalPage'));
const UsersAdminPage = lazy(() => import('./pages/UsersAdminPage'));
const CustomersAdminPage = lazy(() => import('./pages/CustomersAdminPage'));
const InvoicesAdminPage = lazy(() => import('./pages/InvoicesAdminPage'));
const KsefPage = lazy(() => import('./pages/KsefPage'));
const AdminEmailManager = lazy(() => import('./components/AdminEmailManager'));
const EmailSettingsPage = lazy(() => import('./pages/EmailSettingsPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const CategoryPage = lazy(() => import('./pages/CategoryPage'));
const PromotionsPage = lazy(() => import('./pages/PromotionsPage'));
const NewProductsPage = lazy(() => import('./pages/NewProductsPage'));
const AdminReturnsPage = lazy(() => import('./pages/AdminReturnsPage'));
const CategoriesAdminPage = lazy(() => import('./pages/CategoriesAdminPage'));
const PromotionsAdminPage = lazy(() => import('./pages/PromotionsAdminPage'));
const QuotesAdminPage = lazy(() => import('./pages/QuotesAdminPage'));
const QuotePage = lazy(() => import('./pages/QuotePage'));
const HomepageAdminPage = lazy(() => import('./pages/HomepageAdminPage'));
const ShippingTiersAdminPage = lazy(() => import('./pages/ShippingTiersAdminPage'));
const InternationalShippingAdminPage = lazy(() => import('./pages/InternationalShippingAdminPage'));
const ETCalculatorPage = lazy(() => import('./pages/ETCalculatorPage'));
const MobileAppAdminPage = lazy(() => import('./pages/MobileAppAdminPage'));
const AppNotificationsAdminPage = lazy(() => import('./pages/AppNotificationsAdminPage'));
const PushAdminPage = lazy(() => import('./pages/PushAdminPage'));
const BankTransferAdminPage = lazy(() => import('./pages/BankTransferAdminPage'));
const VatAdminPage = lazy(() => import('./pages/VatAdminPage'));
const TyreSizeCalculatorPage = lazy(() => import('./pages/TyreSizeCalculatorPage'));
const LiveProductViewerPage = lazy(() => import('./pages/LiveProductViewerPage'));
const WishlistPage = lazy(() => import('./pages/WishlistPage'));
const VehicleFitmentPage = lazy(() => import('./pages/VehicleFitmentPage'));
const VehicleSeoPage = lazy(() => import('./pages/VehicleSeoPage'));
const VehicleSeoHubPage = lazy(() => import('./pages/VehicleSeoHubPage'));
const CertificatesPage = lazy(() => import('./pages/CertificatesPage'));
const GalleryPage = lazy(() => import('./pages/GalleryPage'));
const GalleryAdminPage = lazy(() => import('./pages/GalleryAdminPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

import type { Product } from './data/products';
import { cars } from './data/cars';
import AdminLayout from './layouts/AdminLayout';
import StoreLayout from './layouts/StoreLayout';

import { useStoreBootstrap, useStoreUrl } from './contexts/StoreBootstrap';
import { useSEO } from './utils/useSEO';
import { trackAddToCart, trackBeginCheckout, setGaUserId, restoreGaUserId, initContactTracking } from './utils/gtm';

// Simple Error Boundary
class ErrorBoundary extends React.Component<{children: React.ReactNode}, {hasError: boolean, error: Error | null}> {
    constructor(props: {children: React.ReactNode}) {
        super(props);
        this.state = { hasError: false, error: null };
    }
    static getDerivedStateFromError(error: Error) {
        return { hasError: true, error };
    }
    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        console.error("ErrorBoundary caught error:", error, errorInfo);
    }
    render() {
        if (this.state.hasError) {
            return (
                <div className="min-h-screen bg-red-900 text-white p-10">
                    <h1>Wystąpił błąd aplikacji.</h1>
                    <pre>{this.state.error?.toString()}</pre>
                    <button onClick={() => window.location.reload()} className="bg-white text-black px-4 py-2 mt-4 rounded">Odśwież stronę</button>
                </div>
            );
        }
        return this.props.children;
    }
}


export interface CartItem extends Product {
    quantity: number;
}

export interface User {
    name: string;
    email: string;
    discount_percent?: number;
}

export type AppliedDiscount = {
    code: string;
    discountValue: number;
    type: 'percentage' | 'fixed';
    promotion_id?: number;
    discount_percent?: number;
    discount_amount?: number;
    min_order_value?: number;
    title?: string;
};

const App: React.FC = () => {
    const ADMIN_AUTH_RECHECK_MS = 5 * 60 * 1000;
    const bootstrap = useStoreBootstrap();
    const requestUrl = useStoreUrl();
    const [searchReady, setSearchReady] = useState(!bootstrap);
    const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);
    // Keep the login form hidden until the existing admin cookie has been checked.
    // Otherwise the initial `false` state briefly renders the login page on every
    // admin navigation, even when the session is still valid.
    const [adminAuthChecking, setAdminAuthChecking] = useState(true);
    const adminAuthStateRef = useRef(false);
    const adminAuthCheckedAtRef = useRef(0);
    const [adminRole, setAdminRole] = useState<string>('employee');
    const [currentUser, setCurrentUser] = useState<User | null>(null);
    const [clientFelgusEnabled, setClientFelgusEnabled] = useState<boolean | null>(null);
    const [route, setRoute] = useState(requestUrl.pathname || '/');
    useEffect(() => {
        if (bootstrap) return;
        fetch('/api/settings/felgus_enabled', { cache: 'no-store' })
            .then(res => res.ok ? res.json() : null)
            .then(value => {
                if (value !== null) setClientFelgusEnabled(value === true || value === 'true');
            })
            .catch(() => {});
    }, [bootstrap]);
    useEffect(() => {
        const robots = document.querySelector('meta[name="robots"]');
        const privatePage = /^\/(?:admin(?:\/|$)|cart$|checkout$|login$|register$|account$|wishlist$|reset-password$|confirm-order$|payment(?:\/|$)|quote(?:\/|$))/.test(route);
        const query = new URLSearchParams(window.location.search);
        const filtered = /^\/products(?:\/[^/]+)?$/.test(route) && ['search', 'size', 'width', 'pcd', 'et', 'cb', 'color', 'colour', 'brand', 'category', 'source', 'sort', 'tireWidth', 'aspectRatio', 'season', 'thread', 'hex', 'seat', 'length', 'thickness', 'innerDiameter', 'outerDiameter', 'wheelType'].some(key => Boolean(query.get(key)));
        const unqualifiedVehicle = bootstrap?.page?.kind === 'vehicle' && route === new URL(bootstrap.url, 'https://felgocar.pl').pathname && !bootstrap.page.data.indexable;
        robots?.setAttribute('content', privatePage || filtered || unqualifiedVehicle ? 'noindex, follow' : 'index, follow');
        // Route transitions must not retain the previous page's server schemas.
        document.querySelectorAll('script[data-seo-server]').forEach(el => {
            if (el.getAttribute('data-seo-path') !== route) el.remove();
        });
    }, [route]);

    const [cartReady, setCartReady] = useState(!bootstrap);
    const [cart, setCart] = useState<CartItem[]>(() => {
        if (bootstrap) return [];
        try {
            const raw = localStorage.getItem('felgocar_cart');
            if (raw) return JSON.parse(raw) as CartItem[];
        } catch (e) {
            // ignore parse errors
        }
        return [];
    });

    const [syncError, setSyncError] = useState('');
    const [notification, setNotification] = useState<string | null>(null);
    const [discountCodes, setDiscountCodes] = useState<any[]>([]);
    const [appliedDiscount, setAppliedDiscount] = useState<AppliedDiscount | null>(null);

    const [products, setProducts] = useState<Product[]>(bootstrap?.page?.kind === 'catalog' ? bootstrap.page.data.items : bootstrap?.products || []);
    const productsFullyLoaded = React.useRef(false);
    const [homepageSettings, setHomepageSettings] = useState<any>(bootstrap?.homepageSettings || {
        hero: true,
        wheelSearch: true,
        newProducts: true,
        categoryAluminum: true,
        categoryAccessories: true,
        categorySpacers: true,
        categoryNuts: true,
        categoryHubRings: true,
        categoryLocks: true,
        categoryBolts: true,
        categoryPremium: true,
        categorySteel: true,
        categorySummer: true,
        categoryWinter: true,
        featuredProducts: true,
        promotions: true,
        widgetArea: true,
        blogSection: true,
        trustSection: true
    });

    const parseTargetIds = (value?: string | null) => {
        if (!value) return [];
        try {
            const parsed = JSON.parse(value);
            if (Array.isArray(parsed)) return parsed.map(String).filter(Boolean);
        } catch {}
        return value.split(',').map(part => part.trim()).filter(Boolean);
    };

    const applyPromotionsToProducts = (
        baseProducts: Product[],
        promoProducts: any[],
        promotions: any[]
    ) => {
        const promoById = new Map<string, any>();
        promoProducts.forEach((promo) => {
            if (promo && promo.id) {
                promoById.set(String(promo.id), promo);
            }
        });

        const categoryPromos = (promotions || [])
            .filter((promo) => (promo?.target_type === 'category' || promo?.target_type === 'all') && !promo?.product_id && !promo?.code)
            .sort((a, b) => (b.priority || 0) - (a.priority || 0));

        return baseProducts.map((product) => {
            // Klienci B2B EUR mają ceny kontraktowe — nie nadpisuj cenami PLN z promocji
            if ((product as any).customerPricingType === 'eur_plus') return product;

            const directPromo = promoById.get(String(product.id));
            if (directPromo) {
                return {
                    ...product,
                    ...directPromo,
                    price: directPromo.priceNetto ?? directPromo.price ?? product.price,
                    priceNetto: directPromo.priceNetto ?? product.priceNetto,
                    priceBrutto: directPromo.priceBrutto ?? product.priceBrutto,
                    oldPrice: directPromo.oldPrice ?? product.oldPrice,
                    oldPriceNetto: directPromo.oldPriceNetto ?? product.oldPriceNetto,
                    oldPriceBrutto: directPromo.oldPriceBrutto ?? product.oldPriceBrutto,
                } as Product;
            }

            const matchingPromo = categoryPromos.find((promo) => {
                const ids = parseTargetIds(promo.target_ids);
                if (promo.target_type === 'all') return true;
                return ids.includes(String(product.category || ''));
            });

            if (!matchingPromo) return product;

            const baseNetto = Number(product.priceNetto ?? product.price ?? 0);
            const baseBrutto = Number(product.priceBrutto ?? (baseNetto * 1.23));
            let discountedNetto = baseNetto;
            let discountedBrutto = baseBrutto;

            if (matchingPromo.discount_percentage) {
                discountedNetto = Math.max(0, baseNetto * (1 - matchingPromo.discount_percentage / 100));
                discountedBrutto = Math.max(0, baseBrutto * (1 - matchingPromo.discount_percentage / 100));
            }
            if (matchingPromo.discount_amount) {
                discountedNetto = Math.max(0, discountedNetto - matchingPromo.discount_amount);
                discountedBrutto = Math.max(0, discountedBrutto - matchingPromo.discount_amount);
            }

            // Przelicz ceny EUR proporcjonalnie do rabatu PLN
            const discountRatio = baseNetto > 0 ? discountedNetto / baseNetto : 1;
            const baseEurNetto = (product as any).priceEurNetto;
            const discountedEurNetto = baseEurNetto != null ? baseEurNetto * discountRatio : undefined;

            return {
                ...product,
                price: discountedNetto,
                priceNetto: discountedNetto,
                priceBrutto: discountedBrutto,
                vatAmount: discountedBrutto - discountedNetto,
                oldPrice: baseNetto,
                oldPriceNetto: baseNetto,
                oldPriceBrutto: baseBrutto,
                ...(discountedEurNetto !== undefined ? {
                    priceEurNetto: discountedEurNetto,
                    oldPriceEurNetto: baseEurNetto,
                } : {}),
                promo_label: matchingPromo.label || matchingPromo.title || 'Promocja',
                discount_percentage: matchingPromo.discount_percentage ?? null,
                discount_amount: matchingPromo.discount_amount ?? null,
            } as Product;
        });
    };

    useEffect(() => {
        if (route.startsWith('/quote/')) return; // oferta nie potrzebuje ustawień homepage
        (async () => {
            try {
                const w = window as any;
                const res = w._homepageSettingsPrefetch
                    ? await w._homepageSettingsPrefetch
                    : await fetch('/api/settings/homepage_sections');
                w._homepageSettingsPrefetch = null;
                if (res && res.ok) {
                    const data = await res.json();
                    setHomepageSettings(data);
                }
            } catch (error) {
                console.error('Error fetching homepage settings:', error);
            }
        })();
    }, []);

    useEffect(() => {
        if (!('serviceWorker' in navigator)) return;

        const handleControllerChange = () => {
            if (document.visibilityState === 'visible') {
                window.location.reload();
            }
        };

        navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);

        navigator.serviceWorker.getRegistrations().then((registrations) => {
            registrations.forEach((registration) => registration.update().catch(() => {}));
        }).catch(() => {});

        return () => navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
    }, []);

    // Load products from backend when route changes: admin gets all, store gets only visible
    // Skip entirely for /quote/ - offer page fetches only its own data
    useEffect(() => {
        if (route.startsWith('/quote/')) return;
        const isAdmin = route.startsWith('/admin');
        const needsFullCatalog = route === '/products' || route.startsWith('/products/');
        const needsHomepageProducts = route === '/' || route === '';
        if (!isAdmin && !needsFullCatalog && !needsHomepageProducts) return;
        // Skip re-fetch if we already have full products loaded (avoids 4.3MB re-fetch on every navigation)
        if (!isAdmin && productsFullyLoaded.current) return;
        (async () => {
            try {
                const url = isAdmin
                    ? '/api/products?admin=1'
                    : `/api/products?visible=1&lean=1&limit=${needsFullCatalog ? 10000 : 12}${needsHomepageProducts ? '&minStock=4' : ''}`;
                // Reuse early prefetch if available (started from index.html before React mounted)
                const w = window as any;
                const hasPrefetch = !isAdmin && w._productsPrefetch;
                const [resp, promoResp] = await Promise.all([
                    hasPrefetch ? w._productsPrefetch : fetch(url, { credentials: 'include' }),
                    isAdmin ? Promise.resolve(null) : (w._promotionsPrefetch ? w._promotionsPrefetch : fetch('/api/promotions'))
                ]);
                w._productsPrefetch = null;
                w._promotionsPrefetch = null;
                if (resp && resp.ok) {
                    const data = await resp.json();
                    const list = data.products || data;
                    if (Array.isArray(list)) {
                        let nextProducts = list as Product[];
                        if (!isAdmin && promoResp && promoResp.ok) {
                            try {
                                const promoData = await promoResp.json();
                                nextProducts = applyPromotionsToProducts(
                                    nextProducts,
                                    promoData.products || [],
                                    promoData.promotions || []
                                );
                            } catch {}
                        }
                        setProducts(nextProducts);
                        if (!isAdmin) {
                            if (nextProducts.length >= 100) {
                                // Got full product list — mark as loaded so navigations don't re-fetch
                                productsFullyLoaded.current = true;
                            }
                        }
                        if (isAdmin && list.length === 0) {
                            // auto-seed on first run
                            try {
                                const seed = await fetch('/api/products/seed', { method: 'POST', credentials: 'include' });
                                if (seed.ok) {
                                        const again = await fetch('/api/products?admin=1', { credentials: 'include' });
                                    if (again.ok) {
                                        const seededData = await again.json();
                                        const seeded = seededData.products || seededData;
                                        if (Array.isArray(seeded)) setProducts(seeded as Product[]);
                                    }
                                }
                            } catch {}
                        }
                    }
                }
            } catch {}
        })();
    }, [route]);

    const refreshProducts = async () => {
        productsFullyLoaded.current = false; // Force re-fetch
        try {
            const isAdmin = route.startsWith('/admin');
            const url = isAdmin ? '/api/products?admin=1' : '/api/products?visible=1&lean=1&limit=10000';
            const [resp, promoResp] = await Promise.all([
                fetch(url, { credentials: 'include', cache: 'no-store' }),
                isAdmin ? Promise.resolve(null) : fetch('/api/promotions', { cache: 'no-store' })
            ]);
            if (resp.ok) {
                const data = await resp.json();
                const list = data.products || data;
                if (Array.isArray(list)) {
                    let nextProducts = list as Product[];
                    if (!isAdmin && promoResp && promoResp.ok) {
                        try {
                            const promoData = await promoResp.json();
                            nextProducts = applyPromotionsToProducts(
                                nextProducts,
                                promoData.products || [],
                                promoData.promotions || []
                            );
                        } catch (e) {
                            console.error('Failed to load promotions:', e);
                        }
                    }
                    setProducts(nextProducts);
                }
            }
        } catch (e) {
            console.error('Failed to refresh products:', e);
        }
    };

    const [filters, setFilters] = useState({
        brand: null,
        model: null,
        size: null,
        boltPattern: null,
    });
    const [sortOrder, setSortOrder] = useState('name-asc');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [showSearchResults, setShowSearchResults] = useState(false);
    const [filteredProducts, setFilteredProducts] = useState<Product[]>(products);

    const RESULTS_KEY = 'felgocar_search_results';
    const [wheelSearchResults, setWheelSearchResults] = useState<any[]>(() => {
        try { if (bootstrap) return []; const s = localStorage.getItem('felgocar_search_results'); return s ? (JSON.parse(s).results || []) : []; } catch { return []; }
    });
    const [showWheelSearchResults, setShowWheelSearchResults] = useState<boolean>(() => {
        try { if (bootstrap) return false; const s = localStorage.getItem('felgocar_search_results'); return s ? (JSON.parse(s).show || false) : false; } catch { return false; }
    });
    const [wheelSearchError, setWheelSearchError] = useState('');
    const [staggeredSetup, setStaggeredSetup] = useState<StaggeredSetupData | null>(null);
    const [mixedSets, setMixedSets] = useState<MixedSet[]>([]);
    const advancedSearchAbortRef = useRef<AbortController | null>(null);

    // Helper: detect staggered (different widths for same diameter) from combos
    const detectStaggeredFromCombos = (combos: any[], selectedDiam: number | null): { dia: number; frontWidth: number; rearWidth: number } | null => {
        const widthsByDia: Record<number, Set<number>> = {};
        for (const c of combos) {
            const d = c.rim_diameter;
            const w = Number(c.rim_width);
            if (d && !isNaN(w) && w > 0) {
                if (!widthsByDia[d]) widthsByDia[d] = new Set();
                widthsByDia[d].add(w);
            }
        }
        for (const [diaStr, widths] of Object.entries(widthsByDia)) {
            const dia = parseInt(diaStr);
            if (selectedDiam !== null && dia !== selectedDiam) continue;
            const ws = [...widths].sort((a, b) => a - b);
            if (ws.length >= 2) {
                return { dia, frontWidth: ws[0], rearWidth: ws[ws.length - 1] };
            }
        }
        return null;
    };

    // Helper: find matching brand+model products available in both front and rear widths
    const findMixedSets = (products: any[], frontWidth: number, rearWidth: number): MixedSet[] => {
        const tolerance = 0.4;
        const groups: Record<string, { front: any[]; rear: any[] }> = {};
        for (const p of products) {
            if (!p.brand || !p.model) continue; // skip if missing key fields
            const w = parseFloat(p.rimWidth || p.width || '0');
            if (isNaN(w) || w === 0) continue;
            const diameter = (p.size || '').toString().toLowerCase().replace(/[^0-9r]/g, '');
            const key = `${(p.brand).toLowerCase()}::${(p.model).toLowerCase()}::${diameter}`;
            if (!groups[key]) groups[key] = { front: [], rear: [] };
            if (Math.abs(w - frontWidth) <= tolerance) groups[key].front.push(p);
            if (Math.abs(w - rearWidth) <= tolerance) groups[key].rear.push(p);
        }
        const result: MixedSet[] = [];
        for (const [key, g] of Object.entries(groups)) {
            if (g.front.length === 0 || g.rear.length === 0) continue;
            const front = g.front[0];
            const rear = g.rear[0];
            const frontUnit = front.priceBrutto ?? front.price ?? 0;
            const rearUnit = rear.priceBrutto ?? rear.price ?? 0;
            result.push({
                key,
                brand: front.brand,
                modelName: front.model,
                front,
                rear,
                frontWidth,
                rearWidth,
                combinedPrice: frontUnit * 2 + rearUnit * 2,
            });
        }
        return result.sort((a, b) => a.combinedPrice - b.combinedPrice);
    };

    useEffect(() => {
        if (!bootstrap) return;
        try {
            const saved = JSON.parse(localStorage.getItem('felgocar_search_results') || 'null');
            if (saved && Array.isArray(saved.results)) { setWheelSearchResults(saved.results); setShowWheelSearchResults(Boolean(saved.show)); }
        } catch {}
        setSearchReady(true);
    }, []);

    useEffect(() => {
        if (!searchReady) return;
        try {
            if (showWheelSearchResults && wheelSearchResults.length > 0) {
                localStorage.setItem(RESULTS_KEY, JSON.stringify({ results: wheelSearchResults, show: true }));
            } else {
                localStorage.removeItem(RESULTS_KEY);
            }
        } catch {}
    }, [wheelSearchResults, showWheelSearchResults, searchReady]);

    const checkAdminAuth = useCallback(async (force = false): Promise<boolean> => {
        const checkedRecently = adminAuthStateRef.current
            && Date.now() - adminAuthCheckedAtRef.current < ADMIN_AUTH_RECHECK_MS;
        if (!force && checkedRecently) return true;
        setAdminAuthChecking(true);
        try {
            const resp = await fetch('/api/admin/check', { credentials: 'include' });
            if (resp.ok) {
                const data = await resp.json();
                adminAuthStateRef.current = true;
                adminAuthCheckedAtRef.current = Date.now();
                setIsAdminAuthenticated(true);
                setAdminRole(data?.user?.role || 'employee');
                return true;
            } else {
                adminAuthStateRef.current = false;
                adminAuthCheckedAtRef.current = 0;
                setIsAdminAuthenticated(false);
                setAdminRole('employee');
                return false;
            }
        } catch (e) {
            adminAuthStateRef.current = false;
            adminAuthCheckedAtRef.current = 0;
            setIsAdminAuthenticated(false);
            setAdminRole('employee');
            return false;
        } finally {
            setAdminAuthChecking(false);
        }
    }, []);

    useEffect(() => {
        const handleRouteChange = () => {
            const newRoute = window.location.pathname || '/';
            console.log('Route changed to:', newRoute);
            setRoute(newRoute);
        };
        window.addEventListener('popstate', handleRouteChange);
        return () => window.removeEventListener('popstate', handleRouteChange);
    }, []);

    // Keep the user's current scroll position stable across route changes.
    // Automatic scrolling here caused mobile jumps after filtering/search flows.

    useEffect(() => {
        restoreGaUserId();
        return initContactTracking();
    }, []);

    // Track page visits (only public pages, deduplicated server-side by IP+date+path)
    useEffect(() => {
        if (!route.startsWith('/admin')) {
            fetch('/api/track-visit', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ path: route, referrer: document.referrer || '' }),
            }).catch(() => {});
            (window as any).gtag?.('event', 'page_view', {
                page_title: document.title,
                page_location: window.location.href,
                page_path: route,
            });
        }
    }, [route]);

    // Heartbeat ping co 60s — utrzymuje status "online" nawet gdy user nie nawiguje
    useEffect(() => {
        const ping = () => fetch('/api/ping', { method: 'POST' }).catch(() => {});
        ping(); // od razu przy mount
        const interval = setInterval(ping, 60_000);
        return () => clearInterval(interval);
    }, []);

    // Check admin auth on route change to admin pages
    useEffect(() => {
        if (route.startsWith('/admin')) {
            void checkAdminAuth();
        }
    }, [route, checkAdminAuth]);

    // Revalidate only after the cached admin check becomes stale while the tab
    // was away, rather than blocking every internal admin navigation.
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible' && route.startsWith('/admin')) {
                void checkAdminAuth();
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [route, checkAdminAuth]);

    // On mount, restore customer session from cookie (survives page refresh)
    useEffect(() => {
        fetch('/api/customer/profile', { credentials: 'include' })
            .then(r => r.ok ? r.json() : null)
            .then(data => {
                if (data?.ok && data.customer) {
                    const c = data.customer;
                    const name = c.first_name && c.last_name
                        ? `${c.first_name} ${c.last_name}`
                        : c.username || c.email;
                    setCurrentUser({ name, email: c.email, discount_percent: c.discount_percent });
                }
            })
            .catch(() => {});
    }, []);
    
    useEffect(() => {
        if (notification) {
            const timer = setTimeout(() => setNotification(null), 3000);
            return () => clearTimeout(timer);
        }
    }, [notification]);

    // Obsługa parametrów kompatybilności z URL
    useEffect(() => {
        const checkCompatibilityParams = async () => {
            const urlParams = new URLSearchParams(window.location.search);
            const isCompatible = urlParams.get('compatible') === 'true';
            const brand = urlParams.get('brand');
            const model = urlParams.get('model');
            const year = urlParams.get('year');
            
            if (isCompatible && brand && model) {
                console.log('🎯 Loading compatible wheels for:', { brand, model, year });
                
                try {
                    const url = `/api/products/compatible/${encodeURIComponent(brand)}/${encodeURIComponent(model)}${year ? `?year=${year}` : ''}`;
                    const response = await fetch(url);
                    
                    if (response.ok) {
                        const compatibleWheels = await response.json();
                        console.log(`✅ Found ${compatibleWheels.length} compatible wheels`);
                        
                        // Ustaw wyniki jako wheel search results
                        setWheelSearchResults(compatibleWheels);
                        setShowWheelSearchResults(true);
                        setRoute('/products');
                        
                        // Pokaż informację o kompatybilności
                        showNotification(`Znaleziono ${compatibleWheels.length} felg kompatybilnych z ${brand} ${model}${year ? ` (${year})` : ''}`);
                    } else {
                        console.error('❌ Error fetching compatible wheels:', response.status);
                    }
                } catch (error) {
                    console.error('❌ Error fetching compatible wheels:', error);
                }
            }
        };
        
        checkCompatibilityParams();
    }, []);

     useEffect(() => {
    let prods = [...products];
    // storefront visibility (admin can toggle)
    prods = prods.filter(p => p.visible !== false);
    // hide products without price
    prods = prods.filter(p => Number(p.priceBrutto ?? p.price ?? 0) > 0);

        // Filtering
        if (filters.brand) {
            prods = prods.filter(p => p.brand === filters.brand);
        }
        if (filters.model) {
            const selectedModel = filters.model;
            prods = prods.filter(p => p.name.includes(selectedModel));
        }
        if (filters.size) {
            prods = prods.filter(p => p.size === filters.size);
        }
        if (filters.boltPattern) {
            prods = prods.filter(p => p.boltPattern === filters.boltPattern);
        }

        // Sorting
        switch (sortOrder) {
            case 'price-asc':
                prods.sort((a, b) => a.price - b.price);
                break;
            case 'price-desc':
                prods.sort((a, b) => b.price - a.price);
                break;
            case 'name-asc':
                prods.sort((a, b) => a.name.localeCompare(b.name));
                break;
            case 'name-desc':
                prods.sort((a, b) => b.name.localeCompare(a.name));
                break;
            default:
                break;
        }

        setFilteredProducts(prods);

        // Check if there are active filters and results
        const hasActiveFilters = Object.values(filters).some(value => value !== null);
        setShowSearchResults(hasActiveFilters && prods.length > 0);
    }, [filters, sortOrder, products]);


    const showNotification = (message: string) => {
        setNotification(message);
    };

    // Push notification subscription
    const subscribePush = async (userType: 'admin' | 'customer', customerEmail?: string) => {
        try {
            if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
            const reg = await navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' });
            await navigator.serviceWorker.ready;
            const permission = await Notification.requestPermission();
            if (permission !== 'granted') return;
            const keyResp = await fetch('/api/push/vapid-public-key');
            const { key } = await keyResp.json();
            // Convert base64 VAPID public key to Uint8Array
            const pad = key.replace(/-/g, '+').replace(/_/g, '/');
            const raw = atob(pad);
            const appKey = new Uint8Array(raw.length);
            for (let i = 0; i < raw.length; i++) appKey[i] = raw.charCodeAt(i);
            const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: appKey });
            await fetch('/api/push/subscribe', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ subscription: sub, userType, customerEmail })
            });
        } catch (e) {
            console.warn('Push subscription failed:', e);
        }
    };

    const API_BASE = (import.meta as any)?.env?.VITE_API_BASE_URL || '/api';

    const handleWheelSearch = async (make: string, model: string, year: string) => {
        try {
            setWheelSearchError('');
            const response = await fetch(`${API_BASE}/search/vehicle?make=${encodeURIComponent(make)}&model=${encodeURIComponent(model)}&year=${encodeURIComponent(year)}`);
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const data = await response.json();
            setWheelSearchResults(data.products || []);
            setShowWheelSearchResults(true);
        } catch (error) {
            console.error('Wheel search error:', error);
            setWheelSearchError('Błąd wyszukiwania felg: ' + (error as Error).message);
            setWheelSearchResults([]);
            setShowWheelSearchResults(false);
        }
    };

    const handleAdvancedWheelSearch = async (filters: any) => {
        advancedSearchAbortRef.current?.abort();
        const abortController = new AbortController();
        advancedSearchAbortRef.current = abortController;

        try {
            setWheelSearchResults([]);
            setWheelSearchError('');
            setShowWheelSearchResults(false);
            setMixedSets([]);
            setStaggeredSetup(null);

            const params = new URLSearchParams();
            const setParam = (key: string, value: unknown) => {
                if (value === undefined || value === null || value === '') return;
                params.set(key, String(value));
            };

            setParam('make', filters.make);
            setParam('model', filters.model);
            setParam('year', filters.year);
            setParam('size', filters.size || filters.diameter);
            setParam('width', filters.width);
            setParam('oemCombos', filters.oemCombos);
            setParam('pcd', filters.pcd);
            setParam('et', filters.et);
            setParam('cb', filters.cb);
            setParam('color', filters.color);
            setParam('brand', filters.brand);
            setParam('minPrice', filters.minPrice);
            setParam('maxPrice', filters.maxPrice);
            setParam('inStock', filters.inStock !== undefined ? String(filters.inStock) : 'true');
            params.set('limit', '50000');

            if (filters.make && filters.model && !filters.size && !filters.diameter && !filters.width && !filters.oemCombos) {
                const specsParams = new URLSearchParams({
                    make: String(filters.make),
                    model: String(filters.model)
                });
                if (filters.year) specsParams.set('year', String(filters.year));
                if (filters.trimId) specsParams.set('trimId', String(filters.trimId));

                const specsResp = await fetch(`/api/vehicle-fitment/specs?${specsParams.toString()}`, {
                    signal: abortController.signal
                });
                if (specsResp.ok) {
                    const specsData = await specsResp.json();
                    if (specsData?.ok && Array.isArray(specsData.data) && specsData.data.length > 0) {
                        const combos: Array<{ rim_diameter?: unknown; rim_width?: unknown }> = specsData.data.flatMap(
                            (generation: { combos?: unknown }) => Array.isArray(generation.combos) ? generation.combos : []
                        );
                        const diameters = [...new Set(
                            combos
                                .map((combo) => Number(combo?.rim_diameter))
                                .filter((value): value is number => Number.isFinite(value) && value > 0)
                        )].sort((a, b) => a - b);
                        const widths = [...new Set(
                            combos
                                .map((combo) => Number(combo?.rim_width))
                                .filter((value): value is number => Number.isFinite(value) && value > 0)
                        )].sort((a, b) => a - b);
                        const oemCombos = [...new Set(
                            combos
                                .map((combo) => ({ diameter: Number(combo?.rim_diameter), width: Number(combo?.rim_width) }))
                                .filter((combo) => Number.isFinite(combo.diameter) && combo.diameter > 0 && Number.isFinite(combo.width) && combo.width > 0)
                                .map((combo) => `${combo.diameter}x${combo.width}`)
                        )];

                        if (diameters.length > 0) params.set('size', diameters.join(','));
                        if (widths.length > 0) params.set('width', widths.join(','));
                        if (oemCombos.length > 0) params.set('oemCombos', oemCombos.join('|'));
                    }
                }
            }

            const url = `${API_BASE}/wheels/search-advanced?${params.toString()}`;
            const response = await fetch(url, { signal: abortController.signal });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);

            const data = await response.json();
            if (advancedSearchAbortRef.current !== abortController) return;
            if (!data?.ok) throw new Error(data?.error || 'Błąd odpowiedzi serwera');

            setWheelSearchResults(Array.isArray(data.products) ? data.products : []);
            setShowWheelSearchResults(true);
        } catch (error) {
            if (error instanceof DOMException && error.name === 'AbortError') return;
            console.error('❌ Advanced wheel search error:', error);
            setWheelSearchError('Błąd wyszukiwania felg: ' + (error instanceof Error ? error.message : String(error)));
            setWheelSearchResults([]);
            setShowWheelSearchResults(false);
        } finally {
            if (advancedSearchAbortRef.current === abortController) {
                advancedSearchAbortRef.current = null;
            }
        }
    };

    useEffect(() => {
        setMobileAccount(currentUser ? String(currentUser.email) : null);
        captureCampaign();
        void wishlistStore.syncAccount().catch(reportMobileError);
        const receive = (event: Event) => {
            const value = (event as CustomEvent<MobileCart>).detail;
            setCart(value.items.map(item => ({ ...(products.find(p => String(p.id) === String(item.product_id)) || {}), id: String(item.product_id), slug: String(item.product_id), name: item.name, price: item.price, priceBrutto: item.price, imageUrl: item.image, stock: item.available ? item.stock : 0, quantity: item.quantity, unavailable: !item.available, images: item.image ? [item.image] : [] } as any)));
        };
        const error = (event: Event) => setSyncError(String((event as CustomEvent).detail));
        window.addEventListener('mobile-cart-snapshot', receive);
        window.addEventListener('mobile-sync-error', error);
        const query = new URLSearchParams(window.location.search);
        if (currentUser && query.get('notification')) { void mobileApi('/cart/restore', { method: 'POST', body: JSON.stringify({ notificationId: query.get('notification') }) }).then(() => reloadSharedCart()).catch(reportMobileError); query.delete('notification'); const qs = query.toString(); window.history.replaceState({}, '', window.location.pathname + (qs ? '?' + qs : '')); }
        const refresh = () => { void reloadSharedCart().catch(reportMobileError); void wishlistStore.syncAccount().catch(reportMobileError); };
        window.addEventListener('focus', refresh);
        const crossTab = (event: StorageEvent) => { if (event.key === 'felgocar-account-change') window.location.reload(); };
        window.addEventListener('storage', crossTab);
        if (currentUser) { void reloadSharedCart().catch(reportMobileError); void connectWebPush(false).catch(() => undefined); }
        return () => { window.removeEventListener('mobile-cart-snapshot', receive); window.removeEventListener('mobile-sync-error', error); window.removeEventListener('focus', refresh); window.removeEventListener('storage', crossTab); };
    }, [currentUser, products]);

    // On mount: reconcile local cart with server cart
    useEffect(() => {
        const fetchCart = async () => {
            const account = mobileOwner();
            try {
                if (await sharedCartEnabled()) { await reloadSharedCart(); return; }
                const resp = await fetch('/api/cart', { credentials: 'include' });
                const data = await resp.json().catch(() => ({} as any));
                if (account !== mobileOwner()) return;
                const serverItems = (data?.cart?.items || []) as Array<{ productId: string; quantity: number; price: number; name: string; imageUrl?: string }>
                if (Array.isArray(serverItems)) {
                    // Update local cart from server
                    const restored: CartItem[] = serverItems.map((it: any) => {
                        const p = products.find(x => x.id === it.productId || x.slug === it.productId);
                        const base = p || { 
                            id: it.productId, 
                            name: it.name, 
                            price: it.price, 
                            imageUrl: it.imageUrl || '', 
                            slug: it.productId, 
                            brand: 'Vossen', 
                            images: [], 
                            size: '19"', 
                            boltPattern: '5x112', 
                            rating: 0, 
                            reviews: 0, 
                            description: '' 
                        } as any;
                        return { 
                            ...base, 
                            // Zawsze używaj ceny z serwera (uwzględnia zniżki hurtowe itp.)
                            price: it.price,
                            priceNetto: it.priceNetto ?? (base as any).priceNetto,
                            priceBrutto: it.price,
                            priceEur: it.priceEur ?? (base as any).priceEur,
                            priceEurNetto: it.priceEurNetto ?? (base as any).priceEurNetto,
                            customerPricingType: it.customerPricingType,
                            quantity: it.quantity 
                        };
                    });
                    setCart(restored);
                }
            } catch {}
        };

        // Initial fetch
        fetchCart();

        // Listen for internal cart updates from components
        const handleCartUpdated = () => {
            fetchCart();
        };
        window.addEventListener('cartUpdated', handleCartUpdated);
        
        return () => window.removeEventListener('cartUpdated', handleCartUpdated);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [products]);

    // Dodaje dokładnie podaną liczbę sztuk bez normalizacji do 4
    const addToCartRawLegacy = (product: any, qty: number) => {
        const productForCart = { ...product, price: product.priceBrutto || product.price, quantity: qty };
        setCart(prev => {
            const existing = prev.find(i => i.id === product.id);
            return existing
                ? prev.map(i => i.id === product.id ? { ...i, quantity: i.quantity + qty } : i)
                : [...prev, productForCart];
        });
        trackAddToCart(product, qty);
        fetch('/api/cart/add', { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ productId: product.id, quantity: qty }) }).catch(() => {});
    };

    const syncedChange = (input: Record<string, unknown>, legacy: () => void, message?: string) => {
        void mutateSharedCart(input).then(handled => { if (!handled) legacy(); else if (message) showNotification(message); }).catch(reportMobileError);
    };
    const addToCartRaw = (product: any, qty: number) => syncedChange({ operation: 'add', productId: String(product.id), quantity: qty }, () => addToCartRawLegacy(product, qty), 'Dodano do koszyka!');
    const addToCart = (product: Product, quantity: number) => syncedChange({ operation: 'add', productId: String(product.id), quantity: normalizeToSetOfFour(quantity) }, () => addToCartLegacy(product, quantity), 'Dodano do koszyka!');
    const updateCartQuantity = (productId: string, quantity: number) => syncedChange({ operation: 'set', productId: String(productId), quantity: normalizeToSetOfFour(quantity) }, () => updateCartQuantityLegacy(productId, quantity));
    const removeFromCart = (productId: string) => syncedChange({ operation: 'remove', productId: String(productId) }, () => removeFromCartLegacy(productId));
    const clearCart = () => syncedChange({ operation: 'clear' }, clearCartLegacy);

    const normalizeToSetOfFour = (qty: number) => {
        const safe = Number.isFinite(qty) ? qty : 0;
        return Math.max(4, Math.round(safe / 4) * 4);
    };

    const addToCartLegacy = (product: Product, quantity: number) => {
        const normalizedQty = normalizeToSetOfFour(quantity);
        const currentQuantity = cart.find(item => item.id === product.id)?.quantity || 0;
        if (product.stock !== undefined && product.stock < currentQuantity + normalizedQty) {
            showNotification('Brak wystarczającej ilości w magazynie!');
            return;
        }
        // Tworzymy produkt z ceną brutto jako główną ceną dla koszyka
        const productForCart = {
            ...product,
            price: product.priceBrutto || (product.price * 1.23), // Używamy ceny brutto
            quantity: normalizedQty
        };
        setCart(prevCart => {
            const existingItem = prevCart.find(item => item.id === product.id);
            const next = existingItem
                ? prevCart.map(item => item.id === product.id ? { ...item, quantity: normalizeToSetOfFour(item.quantity + normalizedQty) } : item)
                : [...prevCart, productForCart];
            return next;
        });
        trackAddToCart(product, normalizedQty);
        // sync with server in background
        (async () => { try { await fetch('/api/cart/add', { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ productId: product.id, quantity: normalizedQty }) }); } catch {} })();
        showNotification('Dodano do koszyka!');
    };
    
    const updateCartQuantityLegacy = (productId: string, newQuantity: number) => {
        const normalizedQty = normalizeToSetOfFour(newQuantity);
        const product = products.find(p => p.id === productId);
        if (product && product.stock !== undefined && product.stock < normalizedQty) {
            showNotification('Brak wystarczającej ilości w magazynie!');
            return;
        }
        setCart(prevCart => prevCart.map(item => item.id === productId ? { ...item, quantity: normalizedQty } : item).filter(item => item.quantity > 0));
        (async () => { try { await fetch('/api/cart/update', { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ productId, quantity: normalizedQty }) }); } catch {} })();
    };

    const removeFromCartLegacy = (productId: string) => {
        setCart(prevCart => prevCart.filter(item => item.id !== productId));
        (async () => { try { await fetch('/api/cart/update', { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ productId, quantity: 0 }) }); } catch {} })();
    };

    const clearCartLegacy = () => {
        setCart([]);
        setAppliedDiscount(null);
        (async () => { try { await fetch('/api/cart/clear', { method: 'POST', credentials: 'include' }); } catch {} })();
    };

    useEffect(() => {
        if (!bootstrap) return;
        try {
            const saved = JSON.parse(localStorage.getItem('felgocar_cart') || '[]');
            if (Array.isArray(saved)) setCart(saved);
        } catch {}
        setCartReady(true);
    }, []);

    // Persist cart to localStorage
    useEffect(() => {
        try {
            if (cartReady) localStorage.setItem('felgocar_cart', JSON.stringify(cart));
        } catch (e) {
            // ignore quota errors
        }
    }, [cart, cartReady]);
    
    const applyDiscountCode = async (code: string, orderValue: number): Promise<boolean> => {
        try {
            const response = await fetch('/api/promotions/validate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ code, order_value: orderValue })
            });

            if (response.ok) {
                const data = await response.json();
                if (data?.ok && data?.promotion) {
                    const promo = data.promotion;
                    const discountPercent = promo.discount_percent ?? null;
                    const discountAmount = promo.discount_amount ?? null;
                    const discountValue = discountPercent ?? discountAmount ?? 0;
                    const type = discountPercent ? 'percentage' : 'fixed';
                    setAppliedDiscount({
                        code: promo.code || code,
                        discountValue,
                        type,
                        promotion_id: promo.id,
                        discount_percent: discountPercent ?? undefined,
                        discount_amount: discountAmount ?? undefined,
                        min_order_value: promo.min_order_value ?? undefined,
                        title: promo.title ?? undefined
                    });
                    showNotification(`Kod "${promo.code || code}" został zastosowany!`);
                    return true;
                }
                if (data?.error) {
                    showNotification(data.error);
                    return false;
                }
            }
        } catch (error) {
            console.error('Promo validation failed:', error);
        }

        const codeData = discountCodes.find(c => c.code.toUpperCase() === code.toUpperCase() && c.isActive);
        if (codeData) {
            setAppliedDiscount({ code: codeData.code, discountValue: codeData.value, type: codeData.type });
            showNotification(`Kod "${codeData.code}" został zastosowany!`);
            return true;
        }

        showNotification('Nieprawidłowy lub nieaktywny kod rabatowy.');
        return false;
    };

    const removeDiscount = () => {
        setAppliedDiscount(null);
        showNotification('Usunięto kod rabatowy.');
    };


    
    const handleProductAdd = async (newProduct: Product) => {
        try {
            const resp = await fetch('/api/products', { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(newProduct) });
            const data = await resp.json().catch(()=>({}));
            if (resp.ok && data?.product) {
                setProducts(prev => [...prev, data.product as Product]);
                showNotification('Produkt został dodany.');
            } else {
                setProducts(prev => [...prev, newProduct]);
                showNotification('Dodano lokalnie (backend niedostępny).');
            }
        } catch {
            setProducts(prev => [...prev, newProduct]);
            showNotification('Dodano lokalnie (błąd połączenia).');
        }
    };

    const handleProductEdit = async (editedProduct: Product) => {
        const prev = products;
        setProducts(p => p.map(x => x.id === editedProduct.id ? editedProduct : x));
        try {
            const resp = await fetch(`/api/products/${encodeURIComponent(editedProduct.id)}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(editedProduct) });
            if (!resp.ok) {
                const errData = await resp.json().catch(() => ({}));
                console.error('Product save failed:', resp.status, errData);
                setProducts(prev); // revert optimistic update
                showNotification(`❌ Błąd zapisu: ${errData.error || resp.statusText}`);
                return;
            }
            const data = await resp.json().catch(() => ({}));
            if (data?.product) {
                setProducts(p => p.map(x => x.id === editedProduct.id ? (data.product as Product) : x));
            }
            showNotification('✅ Produkt został zaktualizowany.');
        } catch (err: any) {
            console.error('Product save error:', err);
            setProducts(prev); // revert optimistic update
            showNotification(`❌ Błąd połączenia: ${err?.message || 'Nie udało się zapisać produktu'}`);
        }
    };

    const handleProductDelete = async (productId: string) => {
        setProducts(prev => prev.filter(p => p.id !== productId));
        try {
            await fetch(`/api/products/${encodeURIComponent(productId)}`, { method: 'DELETE', credentials: 'include' });
        } catch {}
        showNotification('Produkt został usunięty.');
    };


    const handleAdminLogin = async () => {
        // after successful login the server sets HttpOnly cookie; verify it
        const authed = await checkAdminAuth(true);
        if (authed && !window.location.pathname.startsWith('/admin')) {
            navigateTo('/admin/dashboard');
        }
        // Subscribe to push notifications for admin
        if (authed) subscribePush('admin');
    };

    const handleAdminLogout = async () => {
        try {
            await fetch('/api/admin/logout', { method: 'POST', credentials: 'include' });
        } catch (e) { /* ignore */ }
        setIsAdminAuthenticated(false);
        adminAuthStateRef.current = false;
        adminAuthCheckedAtRef.current = 0;
        setAdminRole('employee');
        window.location.pathname = '/admin';
    };

    const fetchAndSetCart = async () => {
        try {
            if (await sharedCartEnabled()) { await reloadSharedCart(); return; }
            const resp = await fetch('/api/cart', { credentials: 'include' });
            const data = await resp.json().catch(() => ({} as any));
            const serverItems = (data?.cart?.items || []) as Array<{ productId: string; quantity: number; price: number; name: string; imageUrl?: string }>;
            if (Array.isArray(serverItems)) {
                const restored: CartItem[] = serverItems.map(it => {
                    const p = products.find((x: Product) => x.id === it.productId || x.slug === it.productId);
                    return {
                        ...(p || { id: it.productId, name: it.name, price: it.price, imageUrl: it.imageUrl || '', slug: it.productId, brand: '', images: [], size: '19"', boltPattern: '5x112', rating: 0, reviews: 0, description: '' } as any),
                        quantity: it.quantity
                    };
                });
                setCart(restored);
            }
        } catch {}
    };

    const handleCustomerLogin = async (user: User) => {
        setMobileAccount(String(user.email));
        setCart([]);
        localStorage.setItem('felgocar-account-change', String(Date.now()));
        setCurrentUser(user);
        showNotification(`Witaj, ${user.name}!`);
        // Pobierz koszyk przypisany do konta (automatycznie scala z koszykiem anonimowym)
        fetchAndSetCart();
        // Odśwież produkty z nowymi cenami (ceny klienta B2B z cookie) — AWAIT żeby EUR ceny były gotowe przed nawigacją
        await refreshProducts();
        window.dispatchEvent(new CustomEvent('customer-login'));
        navigateTo('/account');
    };

    const handleRegister = (user: User) => {
        setMobileAccount(String(user.email));
        setCart([]);
        localStorage.setItem('felgocar-account-change', String(Date.now()));
        setCurrentUser(user);
        showNotification(`Rejestracja pomyślna! Witaj, ${user.name}!`);
        fetchAndSetCart();
        navigateTo('/account');
    };

    const handleCustomerLogout = async () => {
        setGaUserId(null);
        await disconnectWebPush().catch(() => undefined);
        fetch('/api/customer-logout', { method: 'POST', credentials: 'include' }).catch(() => {});
        setMobileAccount(null);
        localStorage.setItem('felgocar-account-change', String(Date.now()));
        setCurrentUser(null);
        setCart([]);
        localStorage.removeItem('felgocar_cart');
        showNotification('Zostałeś wylogowany.');
        await refreshProducts();
        window.dispatchEvent(new CustomEvent('customer-logout'));
        navigateTo('/');
    };


    const renderContent = () => {
        let content: React.ReactNode = null;
        let adminContent: React.ReactNode = null;

        // Admin-only routes (employee cannot access)
        const adminOnlyRoutes = [
            '/admin/users', '/admin/settings', '/admin/email-settings',
            '/admin/categories', '/admin/pricing', '/admin/promotions',
            '/admin/discounts', '/admin/blog', '/admin/homepage',
            '/admin/allegro-offers', '/admin/olx-adverts',
            '/admin/integrations', '/admin/przelewy24-settings',
            '/admin/allegro-settings', '/admin/dpd-services',
            '/admin/olx-settings', '/admin/facebook-settings',
            '/admin/tiktok-settings', '/admin/wheel-size-api',
            '/admin/fitment',
            '/admin/ksef', '/admin/invoicing', '/admin/et-kalkulator',
            '/admin/app-download', '/admin/app-notifications', '/admin/bank-transfer', '/admin/vat-settings',
            '/admin/live-analytics'
        ];

        // Najpierw obsługa panelu admina
        if (route.startsWith('/admin')) {
            if (adminAuthChecking) {
                return (
                    <div className="fc-admin-login flex items-center justify-center min-h-screen py-12 px-4">
                        <div className="fc-admin-login-card w-full max-w-md p-8 text-center bg-gradient-to-br from-[#242831] to-[#1A1D24] rounded-2xl border border-cyan-600/30 shadow-2xl">
                            <div className="text-cyan-400 text-3xl mb-4" aria-hidden="true">⏳</div>
                            <h2 className="text-xl font-semibold text-white mb-2">Sprawdzam sesję</h2>
                            <p className="text-gray-400">Chwileczkę, trwa weryfikacja dostępu do panelu.</p>
                        </div>
                    </div>
                );
            }
            if (!isAdminAuthenticated) {
                return <LoginPage onLogin={handleAdminLogin} />;
            }

            // Check if current route is admin-only and user is employee
            if (adminRole !== 'admin' && adminOnlyRoutes.includes(route)) {
                return (
                    <AdminLayout onLogout={handleAdminLogout} userRole={adminRole}>
                        <div className="flex items-center justify-center min-h-[60vh]">
                            <div className="text-center p-8 bg-red-500/10 border border-red-500/30 rounded-2xl max-w-md">
                                <div className="text-5xl mb-4">🔒</div>
                                <h2 className="text-xl font-bold text-red-400 mb-2">Brak dostępu</h2>
                                <p className="text-[#8ea6c6]">Ta sekcja jest dostępna tylko dla administratorów.</p>
                                <a href="/admin/dashboard" className="inline-block mt-4 px-4 py-2 bg-cyan-500/20 text-cyan-400 rounded-lg hover:bg-cyan-500/30 transition-colors">
                                    Wróć do pulpitu
                                </a>
                            </div>
                        </div>
                    </AdminLayout>
                );
            }
            
            let adminContent;
            switch(route) {
                case '/admin/invoicing':
                    adminContent = <InvoicingPage showNotification={showNotification} />;
                    break;
                    case '/admin/orders':
                        adminContent = <OrdersPage showNotification={showNotification} />;
                        break;
                    case '/admin/returns':
                        adminContent = <AdminReturnsPage showNotification={showNotification} />;
                        break;
                case '/admin/allegro-offers':
                    adminContent = <AllegroOffersPage />;
                    break;
                case '/admin/olx-orders':
                    adminContent = <OlxOrdersPage />;
                    break;
                case '/admin/olx-adverts':
                    adminContent = <OlxAdvertsPage />;
                    break;
                case '/admin/shipping-tiers':
                    adminContent = <ShippingTiersAdminPage />;
                    break;
                case '/admin/international-shipping':
                    adminContent = <InternationalShippingAdminPage />;
                    break;
                case '/admin/shipping':
                    adminContent = <ShippingPage />;
                    break;
                case '/admin/dpd-shipping':
                    adminContent = <DpdShippingPage showNotification={showNotification} />;
                    break;
                case '/admin/discounts':
                    adminContent = <PromotionsAdminPage />;
                    break;
                case '/admin/products':
                     adminContent = <ProductsAdminPageNew 
                        products={products}
                        onAddProduct={handleProductAdd}
                        onEditProduct={handleProductEdit}
                        onDeleteProduct={handleProductDelete}
                     />;
                     break;
                case '/admin/pricing':
                    adminContent = <PricingAdminPage />;
                    break;
                case '/admin/integrations':
                    adminContent = <IntegrationsPage refreshProducts={refreshProducts} />;
                    break;
                case '/admin/wheel-size-api':
                    adminContent = <WheelSizeApiPage />;
                    break;
                case '/admin/fitment':
                    adminContent = <FitmentAdminPage />;
                    break;
                case '/admin/blog':
                    adminContent = <BlogAdminPage />;
                    break;
                case '/admin/categories':
                    adminContent = <CategoriesAdminPage />;
                    break;
                case '/admin/promotions':
                    adminContent = <PromotionsAdminPage />;
                    break;
                case '/admin/settings':
                    adminContent = <SettingsAdminPage />;
                    break;
                case '/admin/users':
                    adminContent = <UsersAdminPage />;
                    break;
                case '/admin/customers':
                    adminContent = <CustomersAdminPage />;
                    break;
                case '/admin/invoices':
                    adminContent = <InvoicesAdminPage />;
                    break;
                case '/admin/ksef':
                    adminContent = <KsefPage />;
                    break;
                case '/admin/quotes':
                    adminContent = <QuotesAdminPage initialProducts={products} />;
                    break;
                case '/admin/homepage':
                    adminContent = <HomepageAdminPage />;
                    break;
                case '/admin/email':
                    adminContent = <AdminEmailManager />;
                    break;
                case '/admin/email-settings':
                    adminContent = <EmailSettingsPage />;
                    break;
                case '/admin/ai-background-lab':
                    adminContent = <AiBackgroundLabPage />;
                    break;
                case '/admin/bulk-bg-removal':
                    adminContent = <BulkBgRemovalPage />;
                    break;
                case '/admin/przelewy24-settings':
                    adminContent = <Przelewy24SettingsPage />;
                    break;
                case '/admin/allegro-settings':
                    adminContent = <AllegroSettingsPage />;
                    break;
                case '/admin/dpd-services':
                    adminContent = <DpdServicesPage />;
                    break;
                case '/admin/olx-settings':
                    adminContent = <OLXSettingsPage />;
                    break;
                case '/admin/facebook-settings':
                    adminContent = <FacebookSettingsPage />;
                    break;
                case '/admin/tiktok-settings':
                    adminContent = <TikTokSettingsPage />;
                    break;
                // Można dodać case'y dla '/admin/integrations' w przyszłości
                case '/admin/et-kalkulator':
                    adminContent = <ETCalculatorPage />;
                    break;
                case '/admin/app-download':
                    adminContent = <MobileAppAdminPage />;
                    break;
                case '/admin/app-notifications':
                    adminContent = <AppNotificationsAdminPage />;
                    break;
                case '/admin/push-notifications':
                    adminContent = <PushAdminPage />;
                    break;
                case '/admin/bank-transfer':
                    adminContent = <BankTransferAdminPage />;
                    break;
                case '/admin/vat-settings':
                    adminContent = <VatAdminPage />;
                    break;
                case '/admin/live-analytics':
                    adminContent = <LiveProductViewerPage />;
                    break;
                case '/admin/gallery':
                    adminContent = <GalleryAdminPage />;
                    break;
                case '/admin/dashboard':
                default:
                    adminContent = <DashboardPage refreshProducts={refreshProducts} />;
            }
            
            return <AdminLayout onLogout={handleAdminLogout} userRole={adminRole}>{adminContent}</AdminLayout>;
        }

        // Obsługa publicznej strony oferty (poza panelem admina)
        if (route.startsWith('/quote/')) {
            return <QuotePage />;
        }
        
        // Payment return pages
        // Payment pages are handled in the main content switch below
        
        // Obsługa pozostałych stron
        if (route.startsWith('/blog/')) {
            const blogSlug = route.split('/')[2];
            if (blogSlug) {
                content = <BlogPostPage slug={blogSlug} />;
            }
        } else if (route.startsWith('/product/')) {
            const productSlug = route.split('/')[2];
            content = <ProductDetailPage key={route} product={bootstrap?.page?.kind === 'product' && requestUrl.pathname === new URL(bootstrap.url, requestUrl.origin).pathname ? bootstrap.page.data : undefined} onAddToCart={addToCart} />;
        } else if (route === '/cart') {
            content = <CartPage 
                cartItems={cart} 
                onUpdateQuantity={updateCartQuantity} 
                onRemoveItem={removeFromCart} 
                appliedDiscount={appliedDiscount}
                onApplyDiscount={applyDiscountCode}
                onRemoveDiscount={removeDiscount}
            />;
        } else if (route === '/checkout') {
            content = <CheckoutPage 
                cartItems={cart} 
                onClearCart={clearCart} 
                showNotification={showNotification}
                appliedDiscount={appliedDiscount}
            />;
        } else if (route === '/login') {
            content = <CustomerLoginPage onLogin={handleCustomerLogin} />;
        } else if (route === '/privacy') {
            content = <PrivacyPage />;
        } else if (route === '/usun-konto') {
            content = <AccountDeletionPage />;
        } else if (route === '/terms') {
            content = <TermsPage />;
        } else if (route === '/contact') {
            content = <ContactPage />;
        } else if (route === '/shipping') {
            content = <DeliveryInfoPage />;
        } else if (route === '/reklamacja') {
            content = <ComplaintPage />;
        } else if (route === '/returns') {
            content = <ReturnsPage />;
        } else if (route === '/faq') {
            content = <FAQPage />;
        } else if (route === '/payment/success') {
            content = <PaymentSuccessPage />;
        } else if (route === '/payment/error') {
            content = <PaymentErrorPage />;
        } else if (route === '/payment/transfer') {
            content = <PaymentTransferPage />;
        } else if (route === '/confirm-order') {
            content = <ConfirmOrderPage />;
        } else if (route === '/register') {
            content = <RegisterPage onRegister={handleRegister} />;
        } else if (route === '/reset-password') {
            content = <ResetPasswordPage />;
        } else if (route === '/account') {
            content = currentUser ? <MyAccountPage user={currentUser} /> : <CustomerLoginPage onLogin={handleCustomerLogin} />;
        } else if (route === '/wyszukiwacz') {
            content = <WyszukiwaczPage />;
        } else if (route === '/blog') {
            content = <BlogPage />;
        } else if (route.startsWith('/products/')) {
            // Category-specific products page
            const slug = route.replace('/products/', '');
            content = <CategoryPage key={slug} slug={slug} addToCartRaw={(product: any, qty: number) => { addToCartRaw(product, qty); showNotification(`Dodano ${qty} szt. do koszyka!`); }} />;
        } else if (route === '/promotions') {
            content = <PromotionsPage />;
        } else if (route === '/products') {
                 console.log('🔵 RENDERING /products route:', { showWheelSearchResults, wheelSearchResultsCount: wheelSearchResults.length, isWheelSearchActive: showWheelSearchResults });
                 content = (
                    <div className="mt-12">
                        <ProductsPage 
                            products={showWheelSearchResults ? wheelSearchResults : filteredProducts} 
                            sortOrder={sortOrder} 
                            setSortOrder={setSortOrder} 
                            categoryFilter={categoryFilter} 
                            setCategoryFilter={setCategoryFilter}
                            isWheelSearchActive={showWheelSearchResults}
                            addToCartRaw={(product: any, qty: number) => { addToCartRaw(product, qty); showNotification(`Dodano ${qty} szt. do koszyka!`); }}
                            onResetWheelSearch={() => {
                                localStorage.removeItem(RESULTS_KEY);
                                setWheelSearchResults([]);
                                setShowWheelSearchResults(false);
                                setWheelSearchError('');
                                setStaggeredSetup(null);
                                setMixedSets([]);
                            }}
                        />
                    </div>
                );
        } else if (route === '/newproducts') {
                 content = <NewProductsPage />;
        } else if (route === '/kalkulator-et') {
            content = <ETCalculatorPage />;
        } else if (route === '/kalkulator-opon') {
            content = <TyreSizeCalculatorPage />;
        } else if (route === '/certificates') {
            content = <CertificatesPage />;
        } else if (/^\/felgi-aluminiowe\/\d{2}-cali(?:\/\d+x\d+(?:-\d+)?)?$/.test(route)) {
            const [, , diameterSlug, pcdSlug] = route.split('/');
            content = <WheelParameterSeoPage
                diameterSlug={diameterSlug}
                pcdSlug={pcdSlug}
                onAddToCart={(product, quantity) => {
                    addToCartRaw(product, quantity);
                    showNotification(`Dodano ${quantity} szt. do koszyka!`);
                }}
            />;
        } else if (/^\/felgi-do-samochodu\/[^/]+\/[^/]+\/[^/]+$/.test(route)) {
            const [, , makeSlug, modelSlug, generationSlug] = route.split('/');
            content = <VehicleSeoPage
                makeSlug={makeSlug}
                modelSlug={modelSlug}
                generationSlug={generationSlug}
                onAddToCart={(product, quantity) => {
                    addToCartRaw(product, quantity);
                    showNotification(`Dodano ${quantity} szt. do koszyka!`);
                }}
            />;
        } else if (/^\/felgi-do-samochodu(?:\/[^/]+(?:\/[^/]+)?)?$/.test(route)) {
            const [, , makeSlug, modelSlug] = route.split('/');
            content = <VehicleSeoHubPage makeSlug={makeSlug} modelSlug={modelSlug} />;
        } else if (route === '/dobor-felg') {
            content = <VehicleFitmentPage onAddToCart={(p, qty) => { addToCartRaw(p, qty); showNotification(`Dodano ${qty} szt. do koszyka!`); }} />;
        } else if (route === '/galeria-klientow') {
            content = <GalleryPage />;
        } else if (route === '/wishlist') {
            content = <WishlistPage />;
        } else if (route === '/' || route === '') { // Homepage
            content = (
                <HomePage 
                    homepageSettings={homepageSettings}
                    showWheelSearchResults={showWheelSearchResults}
                    activeWheelSearchResults={wheelSearchResults}
                    staggeredSetup={staggeredSetup}
                    mixedSets={mixedSets}
                    showSearchResults={showSearchResults}
                    filteredProducts={filteredProducts}
                    products={products}
                    sortOrder={sortOrder}
                    setSortOrder={setSortOrder}
                    handleAdvancedWheelSearch={handleAdvancedWheelSearch}
                    onResetWheelSearch={() => {
                        localStorage.removeItem(RESULTS_KEY);
                        setWheelSearchResults([]);
                        setShowWheelSearchResults(false);
                        setWheelSearchError('');
                        setStaggeredSetup(null);
                        setMixedSets([]);
                    }}
                    addToCart={addToCart}
                    addToCartRaw={(product: any, qty: number) => { addToCartRaw(product, qty); showNotification(`Dodano ${qty} szt. do koszyka!`); }}
                    onAddMixedSetToCart={(set: MixedSet) => {
                        addToCartRaw(set.front, 2);
                        addToCartRaw(set.rear, 2);
                        showNotification('Dodano komplet mieszany 2+2 do koszyka!');
                    }}
                />
            );
        } else {
            content = <NotFoundPage />;
        }

        // Return the content wrapped in the layout

        return (
            <div className="bg-[#0b1220] text-gray-100 min-h-screen">
                {syncError && <div role="alert" className="fixed bottom-5 left-5 right-5 bg-red-950 text-white p-4 rounded-xl shadow-xl z-[1000] flex flex-wrap gap-3 items-center"><span>{syncError}</span><button className="underline" onClick={() => void reloadSharedCart().then(() => setSyncError('')).catch(reportMobileError)}>Wczytaj aktualny koszyk</button><button className="underline" onClick={() => setSyncError('')}>Zamknij</button></div>}
                {notification && (
                    <div className="fixed top-5 right-5 bg-green-500 text-white py-2 px-4 rounded-lg shadow-lg z-[999] animate-fade-in-out">
                        {notification}
                    </div>
                )}
                <StoreLayout
                    cart={cart}
                    currentUser={currentUser}
                    onLogout={handleCustomerLogout}
                    felgusEnabled={bootstrap ? bootstrap.felgusEnabled !== false : clientFelgusEnabled !== false}
                >
                    {content}
                </StoreLayout>
            </div>
        );
    };

    return (
        <LanguageProvider>
            <MaintenanceMode>
                <VacationBanner />
                <ErrorBoundary key={route}>
                    <Suspense fallback={<PageLoader />}>
                        {renderContent()}
                    </Suspense>
                </ErrorBoundary>
            </MaintenanceMode>
            <CookieConsent />
            <EasterPopup />
            <PushConsent />
        </LanguageProvider>
    );
}

export default App;
